import { Card, Tag, Title, Text, ProgressBar } from '@nimbus-ds/components'
import {
  ArrowupIcon,
  ArrowDownIcon,
  ArrowsHorizontalIcon,
  ClockIcon,
  UserGroupIcon,
  StarIcon,
  CashIcon,
} from '@nimbus-ds/icons'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn, formatCurrency, formatPercentage } from '@/lib/utils'
import { computeTrend, projectExperimentMonthlyGain } from '@/lib/economic-calculations'
import type { Experiment } from '@/types'

interface ActiveExperimentCardProps {
  experiment: Experiment
  results: any | null
  storeAOV?: number
  onClick: () => void
}

// Distinct, high-contrast hues per variant. Control (index 0) reads as the neutral
// baseline (slate); treatment (index 1) gets the primary color. Green is reserved
// for the winner override, so it's intentionally absent here.
const VARIANT_COLORS = [
  'bg-slate-500',
  'bg-[var(--nimbus-colors-primary-interactive)]',
  'bg-amber-500',
  'bg-purple-500',
  'bg-rose-500',
]

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') return <ArrowupIcon size={16} className="text-green-600" />
  if (trend === 'down') return <ArrowDownIcon size={16} className="text-red-500" />
  return <ArrowsHorizontalIcon size={16} className="text-muted-foreground" />
}

export function ActiveExperimentCard({ experiment, results, storeAOV = 0, onClick }: ActiveExperimentCardProps) {
  const conversionRates = results?.conversion_rate as Record<string, number> | undefined
  const confidence = results?.confidence ?? 0
  const daysRunning = results?.duration?.days_running ?? 0
  const trend = computeTrend(results?.time_series)
  const sessionsByVariant = results?.sessions as Record<string, number> | undefined
  const totalSessions =
    results?.summary?.total_sessions ??
    (sessionsByVariant ? Object.values(sessionsByVariant).reduce((a, b) => a + b, 0) : 0)

  const isSignificant = Boolean(results?.summary?.is_significant && results?.summary?.winner)
  const winnerVariant: string | null = isSignificant ? results.summary.winner : null

  const projection = projectExperimentMonthlyGain(results, storeAOV)

  // Find max rate for proportional bars
  const rates = conversionRates ? Object.values(conversionRates) : []
  const maxRate = Math.max(...rates, 0.001)

  return (
    <Card
      className={cn(
        'cursor-pointer transition-shadow hover:shadow-md',
        isSignificant && 'border-l-4 border-l-green-500 bg-green-50/30 ring-1 ring-green-500/10'
      )}
      onClick={onClick}
    >
      <Card.Header className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <Title as="h3" className="leading-tight line-clamp-2">
            {experiment.name}
          </Title>
          <div className="flex shrink-0 items-center gap-1.5">
            <TrendIcon trend={trend} />
            {totalSessions > 0 && (
              <div className="shrink-0 whitespace-nowrap">
                <Tag appearance="neutral">
                  <UserGroupIcon size={12} className="mr-1" />
                  {totalSessions.toLocaleString()}
                </Tag>
              </div>
            )}
            <div className="shrink-0 whitespace-nowrap">
              <Tag appearance="neutral">
                <ClockIcon size={12} className="mr-1" />
                {daysRunning}d
              </Tag>
            </div>
          </div>
        </div>
        {isSignificant && (
          <div className="mt-2 flex items-center gap-1.5">
            <Tag appearance="success">
              <StarIcon size={12} className="mr-1" />
              Significativo &middot; {winnerVariant}
            </Tag>
          </div>
        )}
      </Card.Header>

      <Card.Body className="space-y-3">
        {/* Variant conversion rate bars */}
        {conversionRates && (
          <div className="space-y-1.5">
            {Object.entries(conversionRates).map(([variant, rate], i) => {
              const isWinner = variant === winnerVariant
              return (
                <div
                  key={variant}
                  className={cn(
                    'flex items-center gap-2 text-xs',
                    isWinner && 'font-medium'
                  )}
                >
                  <span
                    className={cn(
                      'w-16 truncate',
                      isWinner ? 'text-green-700' : 'text-muted-foreground'
                    )}
                    title={variant}
                  >
                    {isWinner && <StarIcon size={12} className="mr-1 inline" />}
                    {variant}
                  </span>
                  <div
                    className={cn(
                      'relative h-4 flex-1 overflow-hidden rounded-full bg-muted',
                      isWinner && 'ring-1 ring-green-500/60'
                    )}
                  >
                    <div
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-full',
                        isWinner ? 'bg-green-500' : VARIANT_COLORS[i % VARIANT_COLORS.length]
                      )}
                      style={{ width: `${Math.max((rate / maxRate) * 100, 2)}%` }}
                    />
                  </div>
                  <span
                    className={cn(
                      'w-12 text-right tabular-nums',
                      isWinner ? 'text-green-700' : 'font-medium'
                    )}
                  >
                    {formatPercentage(rate * 100)}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {/* Confidence progress toward 95% */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <Text fontSize="caption">Confianza</Text>
            <span
              className={cn(
                'font-medium tabular-nums',
                isSignificant && 'text-green-700'
              )}
            >
              {formatPercentage(confidence * 100)} / 95%
            </span>
          </div>
          <ProgressBar
            value={Math.min((confidence / 0.95) * 100, 100)}
            appearance={isSignificant ? 'success' : 'primary'}
          />
        </div>

        {/* Projected monthly impact */}
        {projection && projection.projectedMonthlyGain > 0 && (
          <TooltipProvider>
            <Tooltip delayDuration={150}>
              <TooltipTrigger
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={cn(
                    'flex cursor-help items-center justify-between rounded-md border px-2 py-1.5 text-xs',
                    isSignificant
                      ? 'border-green-200 bg-green-50/60'
                      : 'border-muted bg-muted/30'
                  )}
                >
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <CashIcon size={12} />
                    Proyección /mes
                    {projection.usedStoreAOVFallback && (
                      <span className="ml-1 text-muted-foreground/70">(AOV tienda)</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'font-semibold tabular-nums',
                      isSignificant ? 'text-green-700' : 'text-foreground'
                    )}
                  >
                    +{formatCurrency(projection.projectedMonthlyGain)}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs space-y-1.5">
                <p className="font-medium">Cómo se calcula</p>
                <div className="space-y-0.5 text-[11px] text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>Lift absoluto ({projection.bestVariant})</span>
                    <span className="font-medium tabular-nums text-foreground">
                      {formatPercentage(projection.liftAbsolute * 100)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>Sesiones del experimento /mes</span>
                    <span className="font-medium tabular-nums text-foreground">
                      {Math.round(projection.experimentMonthlySessions).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>AOV {projection.usedStoreAOVFallback ? 'tienda' : projection.bestVariant}</span>
                    <span className="font-medium tabular-nums text-foreground">
                      {formatCurrency(projection.treatmentAOV)}
                    </span>
                  </div>
                </div>
                <p className="border-t pt-1.5 text-[11px] text-muted-foreground">
                  lift × sesiones × AOV ={' '}
                  <span className="font-semibold text-foreground">
                    {formatCurrency(projection.projectedMonthlyGain)}
                  </span>
                </p>
                {projection.usedStoreAOVFallback && (
                  <p className="text-[11px] text-muted-foreground">
                    Usando AOV de la tienda &mdash; el experimento aún no tiene órdenes propias.
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Card.Body>
    </Card>
  )
}
